'use client'

import { cn } from '@/lib/tailwind'
import { WaveformIcon, CircleNotchIcon, CheckIcon } from '@phosphor-icons/react'
import type { LabelSuggestion } from './types'

interface LabelSuggestionsProps {
  suggestions: LabelSuggestion[]
  onToggle: (externalId: string) => void
  isChecking?: boolean
  disabled?: boolean
}

/**
 * Label suggestions from artist releases (for Discogs artists)
 */
export function LabelSuggestions({
  suggestions,
  onToggle,
  isChecking = false,
  disabled = false,
}: LabelSuggestionsProps) {
  if (suggestions.length === 0) return null

  const hasSelectedLabels = suggestions.some(
    (l) => l.isSelected && !l.existingLabelId,
  )

  return (
    <div className="border-t px-3 py-2">
      <div className="mb-1.5 flex items-center gap-1.5">
        <WaveformIcon className="text-muted-foreground h-3.5 w-3.5" />
        <span className="text-muted-foreground text-xs font-medium">
          Labels from releases
        </span>
        {isChecking && (
          <CircleNotchIcon className="text-muted-foreground h-3 w-3 animate-spin" />
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((label) => {
          const isInLibrary = !!label.existingLabelId
          return (
            <button
              key={label.externalId}
              type="button"
              onClick={() => onToggle(label.externalId)}
              disabled={isInLibrary || disabled}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors',
                isInLibrary
                  ? 'bg-muted text-muted-foreground cursor-default'
                  : label.isSelected
                    ? 'bg-primary/15 text-primary border-primary/30 border'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 border border-transparent',
              )}
              title={
                isInLibrary
                  ? 'Already in your library'
                  : label.isSelected
                    ? 'Click to remove'
                    : 'Click to add to library'
              }
            >
              {isInLibrary ? (
                <CheckIcon className="h-3 w-3" />
              ) : label.isSelected ? (
                <CheckIcon className="h-3 w-3" />
              ) : (
                <span className="h-3 w-3 text-center leading-3">+</span>
              )}
              <span className="max-w-[100px] truncate">{label.name}</span>
            </button>
          )
        })}
      </div>
      {hasSelectedLabels && (
        <p className="text-muted-foreground mt-1.5 text-[10px]">
          Selected labels will be added to your library
        </p>
      )}
    </div>
  )
}
